/**
 * Group requests on qv-data: anyone asks for a group, the owner decides
 * (`docs/plans/group-requests.md`, step 1).
 *
 * Two audiences, two path shapes. The **requester's** calls are
 * `/groups/requests*` and carry `?userId=` like `api/billing.ts`, because they
 * act on this account's own rows. The **admin's** are `/admin/group-requests*`
 * like `api/admin.ts`, guarded server-side by the `admin` role and scoped to
 * the admin's own brand — the server answers 404 for another brand's request,
 * so nothing here filters.
 *
 * **Reads degrade, writes throw** — `api/admin.ts`'s split. A list that fails
 * to load is an empty list; an approval that failed quietly would leave the
 * owner believing a group exists that does not. A thrown error carries the
 * server's `detail` sentence, which names the conflict (the key is taken, the
 * notes are missing) rather than a status code.
 */

import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

export type SellAs = "q" | "o";
export type RequestStatus = "pending" | "approved" | "declined";
export type CutKind = "percent" | "fixed";

/** One request as its requester sees it. Never carries the admin's notes. */
export interface GroupRequest {
  key: string;
  status: RequestStatus;
  name: string;
  purpose: string;
  uniqueFeatures: string;
  sellAs: SellAs;
  expectedMembers: number | null;
  contactEmail: string;
  contactPhone: string | null;
  declineReason: string | null;
  /** The group's key once approved; `null` until then. */
  orgKey: string | null;
  createdAt: string;
  decidedAt: string | null;
}

/** The admin's view: the request, plus the account it came from and the notes. */
export interface AdminGroupRequest extends GroupRequest {
  appKey: string;
  adminNotes: string | null;
  decidedBy: string | null;
  account: { key: string; email: string | null; displayName: string | null };
}

export interface AdminGroupRequestList {
  requests: AdminGroupRequest[];
  counts: Record<RequestStatus, number>;
}

export interface NewGroupRequest {
  name: string;
  purpose: string;
  uniqueFeatures: string;
  sellAs: SellAs;
  expectedMembers?: number | null;
  contactEmail: string;
  contactPhone?: string | null;
}

export interface Approval {
  key: string;
  name: string;
  sellAs: SellAs;
  notes: string;
  cutKind?: CutKind;
  /** As typed — "12.5" or "19.99". The server parses it, so one rule decides. */
  cutValue?: string;
}

export function groupRequestsConfigured(): boolean {
  return BASE !== "";
}

function mine(path: string): string {
  return `${BASE}${path}?userId=${encodeURIComponent(currentUserId())}`;
}

async function get<T>(url: string, fallback: T): Promise<T> {
  if (!BASE) return fallback;
  try {
    const response = await fetch(url, { headers: await requestHeaders() });
    if (!response.ok) return fallback;
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

async function write<T>(url: string, method: string, body: unknown): Promise<T> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(url, {
    method,
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const parsed = (await response.json().catch(() => null)) as { detail?: unknown } | null;
    const detail = typeof parsed?.detail === "string" ? parsed.detail : null;
    throw new Error(detail ?? `${method} ${url.replace(BASE, "")} failed (${response.status})`);
  }
  return (await response.json()) as T;
}

// ── the requester ───────────────────────────────────────────────────────────

/** Send a request. Throws with the server's reason (a field, or the pending cap). */
export function createGroupRequest(request: NewGroupRequest): Promise<GroupRequest> {
  return write<GroupRequest>(mine("/groups/requests"), "POST", request);
}

/**
 * This account's requests, newest first. Empty on any failure. The server
 * answers `{requests: [...]}`; unwrapped here so no caller sees the envelope.
 */
export async function myGroupRequests(): Promise<GroupRequest[]> {
  const body = await get<{ requests?: unknown } | null>(mine("/groups/requests/mine"), null);
  return Array.isArray(body?.requests) ? (body.requests as GroupRequest[]) : [];
}

// ── the admin ───────────────────────────────────────────────────────────────

const admin = (path: string): string => `${BASE}/admin/group-requests${path}`;

/** The list for one status (or all), with counts per status. `null` on failure. */
export function listGroupRequests(
  status: RequestStatus | "all" = "pending",
): Promise<AdminGroupRequestList | null> {
  return get<AdminGroupRequestList | null>(admin(`?status=${status}`), null);
}

export function getGroupRequest(key: string): Promise<AdminGroupRequest | null> {
  return get<AdminGroupRequest | null>(admin(`/${encodeURIComponent(key)}`), null);
}

export function saveGroupRequestNotes(key: string, notes: string): Promise<AdminGroupRequest> {
  return write<AdminGroupRequest>(admin(`/${encodeURIComponent(key)}/notes`), "PUT", { notes });
}

export function approveGroupRequest(key: string, approval: Approval): Promise<AdminGroupRequest> {
  return write<AdminGroupRequest>(admin(`/${encodeURIComponent(key)}/approve`), "POST", approval);
}

export function declineGroupRequest(key: string, reason: string): Promise<AdminGroupRequest> {
  return write<AdminGroupRequest>(admin(`/${encodeURIComponent(key)}/decline`), "POST", { reason });
}

/**
 * The key a new group is offered: `grp-` and the name, slugged to the server's
 * rule `^grp-[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$`. A suggestion only — the admin
 * edits it, and the server is the one that refuses a bad or taken key.
 */
export function suggestGroupKey(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
  return `grp-${slug}`;
}

/** The server's key rule, mirrored so the button can say no before a round trip. */
export const GROUP_KEY_PATTERN = /^grp-[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/;
